import * as Sentry from "@sentry/vue"

import type { SelfInfoResponseDatagramBuf } from "~/classes/generated/response"

import { DatagramBuf, DatagramErrorTypeBuf } from "~/classes/generated/datagram"
import { RequestDatagramBuf, SelfInfoRequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId } from "~/utils/flatbuffers/datagram"
import { parseFile } from "~/utils/flatbuffers/file"
import { parseUser } from "~/utils/flatbuffers/user"
import { maybe } from "~/utils/helpers"

const console = useLogger("user/info", theme.colors.pink.hex)

export function sendSelfInfoRequest(request: SelfInfoRequestDatagram) {
  const { sendWorkerDatagramBuf } = useCache()

  const fbb = new DatagramBufferBuilder(64)
  const requestId = addRequestIdToBuf(fbb, request.requestId)
  SelfInfoRequestDatagramBuf.startSelfInfoRequestDatagramBuf(fbb)
  SelfInfoRequestDatagramBuf.addRequestId(fbb, requestId)
  SelfInfoRequestDatagramBuf.addOffline(fbb, request.offline)
  const infoRequest = SelfInfoRequestDatagramBuf.endSelfInfoRequestDatagramBuf(fbb)

  RequestDatagramBuf.startRequestDatagramBuf(fbb)
  RequestDatagramBuf.addSelfInfoRequest(fbb, infoRequest)
  const requests = DatagramBuf.createRequestVector(fbb, [RequestDatagramBuf.endRequestDatagramBuf(fbb)])
  DatagramBuf.startDatagramBuf(fbb)
  DatagramBuf.addRequest(fbb, requests)
  fbb.finish(DatagramBuf.endDatagramBuf(fbb))
  sendWorkerDatagramBuf(fbb.asUint8Array())
}

export function handleSelfInfoResponseDatagramBuf(datagram: SelfInfoResponseDatagramBuf) {
  onSelfInfoResponseDatagram({
    requestId: parseRequestId(datagram.requestId()!),
    offline: datagram.offline(),
    error: maybe(parseDatagramError)(datagram.error()),
    home: maybe(parseFile)(datagram.home()),
    archive: maybe(parseFile)(datagram.archive()),
    user: maybe(parseUser)(datagram.user()),
  })
}

export function onSelfInfoResponseDatagram(datagram: SelfInfoResponseDatagram) {
  const { emitInterfaceEvent, emitEntityEvent } = useEvents()

  emitInterfaceEvent("selfInfoResponse", datagram, datagram.requestId)

  const { logger } = Sentry

  logger.info("Self info response", datagram)

  if (datagram.error || !datagram.home || !datagram.archive || !datagram.user) {
    if (datagram.error && datagram.error.type === DatagramErrorTypeBuf.UserNotFound)
      return console.debug("Self info request failed: user not found")
    else
      return console.error("Self info request failed", datagram)
  }

  emitEntityEvent("fileProperties", datagram.home.fileId, datagram.home, datagram.offline)
  emitEntityEvent("fileProperties", datagram.archive.fileId, datagram.archive, datagram.offline)
  emitEntityEvent("userProperties", datagram.user.userId, datagram.user, datagram.offline)
}
